import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { money, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { printApplication, DEFAULT_DECLINE } from "@/pages/landlord/printApplication";
import { Building2, FileText, Wrench, DollarSign } from "lucide-react";
import { toast } from "sonner";

const EMPTY_PROP = {
  title: "", description: "", address: "", city: "", state: "", zip_code: "",
  property_type: "apartment", bedrooms: 1, bathrooms: 1, square_feet: 0,
  monthly_rent: 0, security_deposit: 0, amenities: "", images: [], available: true,
  application_form: null,
};

const PURGE_DAYS = 30;
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0);

export function useLandlordDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [properties, setProperties] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showings, setShowings] = useState([]);
  const [leases, setLeases] = useState([]);
  const [maintenance, setMaintenance] = useState([]);

  const [propForm, setPropForm] = useState(EMPTY_PROP);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "properties");
  const [editId, setEditId] = useState(null);
  const [propOpen, setPropOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [leaseForm, setLeaseForm] = useState(null);
  const [payout, setPayout] = useState({ paypal_email: "", stripe_onboarded: false, platform_fee_percent: 4, plan_tier: "starter", plan_status: "trialing", billing_interval: "month", trial_end: null, comp_type: null, cancel_at_period_end: false, current_period_end: null, fee_suspended: false, listing_count: 0, listing_limit: 1, tiers: [] });
  const [paypalEmail, setPaypalEmail] = useState("");
  const [venmoHandle, setVenmoHandle] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [billing, setBilling] = useState([]);
  const [declineApp, setDeclineApp] = useState(null);
  const [unread, setUnread] = useState(0);
  const [supportUnread, setSupportUnread] = useState(0);
  const [activity, setActivity] = useState({ applications: 0, showings: 0, leases: 0, maintenance: 0 });
  const [contractors, setContractors] = useState([]);
  const [autoPurgeDays, setAutoPurgeDays] = useState(0);
  const [suggestApp, setSuggestApp] = useState(null);

  const loadContractors = () => api.get("/landlord/contractors").then((r) => setContractors(r.data)).catch(() => {});
  const addContractor = async (c) => {
    try { await api.post("/landlord/contractors", c); toast.success("Contractor saved"); loadContractors(); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save contractor"); return false; }
  };
  const deleteContractor = async (id) => {
    try { await api.delete(`/landlord/contractors/${id}`); loadContractors(); }
    catch (e) { toast.error("Could not remove contractor"); }
  };

  const loadUnread = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  const loadSupportUnread = () => api.get("/support/unread-count").then((r) => setSupportUnread(r.data.count)).catch(() => {});
  const loadActivity = () => api.get("/activity/counts").then((r) => setActivity(r.data)).catch(() => {});
  const loadBilling = () => api.get("/landlord/billing-history").then((r) => setBilling(r.data.items || [])).catch(() => {});

  useEffect(() => {
    if (activeTab === "payouts") loadBilling();
  }, [activeTab]);

  useEffect(() => {
    if (searchParams.get("stripe") === "return") {
      api.get("/landlord/stripe/status").then(({ data }) => {
        if (data.stripe_onboarded) toast.success("Stripe account connected — you can now receive payouts.");
        else toast.info("Stripe onboarding not complete yet. Finish the remaining steps to receive payouts.");
        loadAll();
      }).catch(() => {}).finally(() => {
        const sp = new URLSearchParams(searchParams);
        sp.delete("stripe");
        setSearchParams(sp, { replace: true });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = () => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/properties/mine").then((r) => setProperties(r.data)).catch(() => {});
    api.get("/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/showings").then((r) => setShowings(r.data)).catch(() => {});
    api.get("/leases").then((r) => setLeases(r.data)).catch(() => {});
    api.get("/maintenance").then((r) => setMaintenance(r.data)).catch(() => {});
    api.get("/landlord/payout").then((r) => { setPayout(r.data); setPaypalEmail(r.data.paypal_email || ""); setVenmoHandle(r.data.venmo_handle || ""); }).catch(() => {});
    api.get("/applications/purge-policy").then((r) => setAutoPurgeDays(r.data.days || 0)).catch(() => {});
  };

  const saveAutoPurge = async (days) => {
    const prev = autoPurgeDays;
    setAutoPurgeDays(days);
    try {
      const r = await api.put("/applications/purge-policy", { days });
      toast.success(days > 0
        ? `Declined applications will auto-purge after ${days} days${r.data.purged ? ` · ${r.data.purged} purged now` : ""}`
        : "Auto-purge disabled");
      if (r.data.purged) loadAll();
    } catch (e) {
      setAutoPurgeDays(prev);
      toast.error(e.response?.data?.detail || "Could not update auto-purge policy");
    }
  };

  const savePayout = async () => {
    setBusy(true);
    try {
      await api.put("/landlord/payout", { paypal_email: paypalEmail, venmo_handle: venmoHandle });
      toast.success("Payout settings saved");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const connectStripe = async () => {
    try {
      const { data } = await api.post("/landlord/stripe/connect", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Stripe onboarding");
    }
  };

  const upgradePlan = async (tier, interval = "month") => {
    try {
      const { data } = await api.post("/landlord/upgrade/checkout", { tier, interval, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start subscription");
    }
  };

  const redeemCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    try {
      await api.post("/landlord/coupon/redeem", { code });
      toast.success("Coupon applied — your Pro access is now active!");
      setCouponCode("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply coupon");
    }
  };

  const cancelPlan = async () => {
    try {
      await api.post("/landlord/upgrade/cancel");
      toast.success("Cancellation scheduled — Pro stays active until your billing period ends");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel plan");
    }
  };

  const reactivatePlan = async () => {
    try {
      await api.post("/landlord/upgrade/reactivate");
      toast.success("Monthly plan reactivated — it will keep renewing");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reactivate plan");
    }
  };

  const fmtDate = (iso) => formatDate(iso) || "the end of your billing period";

  useEffect(() => {
    loadAll();
    loadUnread();
    loadSupportUnread();
    loadActivity();
    loadContractors();
    const id = setInterval(() => { loadUnread(); loadSupportUnread(); loadActivity(); }, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveTab(t);
  }, [searchParams]);

  useEffect(() => {
    if (["applications", "showings", "leases", "maintenance"].includes(activeTab)) {
      loadAll();
      api.post(`/activity/seen/${activeTab}`).then(loadActivity).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openCreate = () => {
    setPropForm(EMPTY_PROP);
    setEditId(null);
    setPropOpen(true);
  };

  const openEdit = (p) => {
    setPropForm({ ...p, amenities: (p.amenities || []).join(", "), images: p.images || [] });
    setEditId(p.id);
    setPropOpen(true);
  };

  const onImageFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is over 5MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPropForm((f) => ({ ...f, images: [...f.images, reader.result] }));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeImage = (idx) => setPropForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const saveProperty = async () => {
    setBusy(true);
    const payload = {
      ...propForm,
      bedrooms: parseInt(propForm.bedrooms || 0),
      bathrooms: parseFloat(propForm.bathrooms || 0),
      square_feet: parseInt(propForm.square_feet || 0),
      monthly_rent: parseFloat(propForm.monthly_rent || 0),
      security_deposit: parseFloat(propForm.security_deposit || 0),
      amenities: propForm.amenities ? propForm.amenities.split(",").map((s) => s.trim()).filter(Boolean) : [],
      images: Array.isArray(propForm.images) ? propForm.images : [],
    };
    try {
      if (editId) await api.put(`/properties/${editId}`, payload);
      else await api.post("/properties", payload);
      toast.success(editId ? "Property updated" : "Property listed");
      setPropOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const deleteProperty = async (id) => {
    await api.delete(`/properties/${id}`).catch(() => {});
    toast.success("Property removed");
    loadAll();
  };

  const toggleVisibility = async (p) => {
    try {
      await api.patch(`/properties/${p.id}/visibility`, { hidden: !p.hidden });
      toast.success(!p.hidden ? "Listing hidden from Browse" : "Listing is public again");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update visibility");
    }
  };

  const markFilled = async (id) => {
    try {
      const { data } = await api.post(`/properties/${id}/mark-filled`);
      toast.success(`Listing marked filled${data.declined ? ` · ${data.declined} pending applicant(s) declined` : ""}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark listing filled");
    }
  };

  const deleteApplication = async (appId) => {
    const a = applications.find((x) => x.id === appId);
    const hasLease = a && leases.some((l) => l.property_id === a.property_id && l.renter_id === a.renter_id);
    const warn = hasLease
      ? "⚠️ A lease/tenant record already exists for this applicant. Purging the application will NOT affect that lease — only the applicant's stored data.\n\n"
      : "";
    if (!window.confirm(`${warn}Permanently purge this applicant's data (employment, income, contact details, answers, and any signed document)? A minimal name-only stub is kept for your records. Make sure you've downloaded or printed anything you need — this cannot be undone.`)) return;
    try {
      await api.delete(`/applications/${appId}`);
      toast.success("Applicant data purged — stub retained");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not purge application");
    }
  };

  const bulkPurgeApplications = async () => {
    if (!window.confirm("Purge the applicant data for ALL applications older than 30 days? Sensitive info will be removed and a name-only stub kept for each. Make sure you've downloaded anything you need — this cannot be undone.")) return;
    try {
      const r = await api.post("/applications/purge", { days: 30 });
      toast.success(r.data.purged > 0 ? `Purged ${r.data.purged} application(s)` : "No applications older than 30 days to purge");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not purge applications");
    }
  };

  const deleteSignedApplication = async (appId) => {
    if (!window.confirm("Delete this signed application from the platform? Make sure you've downloaded it first — this cannot be undone.")) return;
    try {
      await api.delete(`/applications/${appId}/signed-document`);
      toast.success("Signed application removed from the platform");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete document");
    }
  };

  const deleteSignedLeaseDoc = async (leaseId, docId) => {
    if (!window.confirm("Delete this signed document from the platform? Make sure you've downloaded it first — this cannot be undone.")) return;
    try {
      await api.delete(`/leases/${leaseId}/documents/${docId}/signed`);
      toast.success("Signed document removed from the platform");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete document");
    }
  };

  const decide = async (id, status, decline_message) => {
    await api.put(`/applications/${id}/decision`, { status, decline_message }).catch(() => {});
    toast.success(`Application ${status}`);
    loadAll();
  };

  const openDecline = (a) => setDeclineApp({ ...a, message: DEFAULT_DECLINE(a) });

  const confirmDecline = async () => {
    setBusy(true);
    try {
      await decide(declineApp.id, "rejected", declineApp.message);
      setDeclineApp(null);
    } finally {
      setBusy(false);
    }
  };

  const respondShowing = async (id, payload) => {
    try {
      await api.post(`/showings/${id}/respond`, payload);
      const msg = { confirm: "Showing confirmed", propose: "New time proposed — sent to the renter", decline: "Showing cancelled", complete: "Showing marked completed" }[payload.action] || "Showing updated";
      toast.success(msg);
      loadAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update showing");
      return false;
    }
  };

  const updateMaint = async (id, status) => {
    await api.put(`/maintenance/${id}`, { status, response: "" }).catch(() => {});
    toast.success("Request updated");
    loadAll();
  };

  const dispatchMaint = async (id, contractor_email, note) => {
    try {
      await api.post(`/maintenance/${id}/dispatch`, { contractor_email, note, origin_url: window.location.origin });
      toast.success(`Request emailed to ${contractor_email}`);
      loadAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the request");
      return false;
    }
  };

  const resendDispatch = async (id, token) => {
    try {
      await api.post(`/maintenance/${id}/resend-dispatch`, { token, origin_url: window.location.origin });
      toast.success("Fresh link sent to the contractor");
      loadAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend the link");
      return false;
    }
  };

  const rateContractor = async (id, token, rating, comment) => {
    try {
      await api.post(`/maintenance/${id}/rate`, { token, rating, comment });
      toast.success("Thanks — rating saved to your address book");
      loadAll();
      loadContractors();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the rating");
      return false;
    }
  };

  const markAvailable = async (id) => {
    try {
      await api.post(`/properties/${id}/mark-available`);
      toast.success("Listing is available again and back on Browse");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update listing");
    }
  };

  const relistUnit = (propertyId) => {
    const p = properties.find((x) => x.id === propertyId);
    if (!p) { toast.error("That listing is no longer available to re-list"); return; }
    setActiveTab("properties");
    setSearchParams({ tab: "properties" }, { replace: true });
    openEdit(p);
    toast.info("Update the listing, then tap “Re-list (mark available)” when it's ready to go back on Browse.");
  };

  const openLeaseFor = (application) => {
    const prop = properties.find((p) => p.id === application.property_id);
    setLeaseForm({
      property_id: application.property_id,
      renter_id: application.renter_id,
      renter_name: application.renter_name,
      property_title: application.property_title,
      start_date: "",
      end_date: "",
      monthly_rent: prop?.monthly_rent || 0,
      security_deposit: prop?.security_deposit || 0,
      other_deposits: 0,
      pet_deposit: 0,
      pet_rent: 0,
      first_month_rent: prop?.monthly_rent || 0,
      lease_term_months: 12,
      terms: "Standard 12-month residential lease. Rent due on the 1st of each month. No smoking. Tenant responsible for utilities unless otherwise stated.",
      lease_doc: null,
      lead_paint: null,
      smoke_co: null,
      info1: null,
      info2: null,
    });
  };

  const sendReminder = async (leaseId) => {
    await api.post(`/leases/${leaseId}/remind`).catch(() => {});
    toast.success("Rent reminder sent to tenant");
  };

  const sendInvoice = async (leaseId) => {
    try {
      await api.post(`/leases/${leaseId}/send-invoice`);
      toast.success("Move-in invoice sent to renter");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invoice");
    }
  };

  const markRentPaid = async (leaseId, method, note) => {
    try {
      await api.post(`/leases/${leaseId}/mark-rent-paid`, { method, note });
      toast.success("Rent payment recorded — the tenant has been notified");
      loadAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record payment");
      return false;
    }
  };

  const endLease = async (leaseId, payload) => {
    try {
      await api.post(`/leases/${leaseId}/end`, payload);
      toast.success("Lease ended — the record is kept in your history");
      loadAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not end the lease");
      return false;
    }
  };

  const addLeaseDocuments = async (leaseId, payload) => {
    try {
      await api.post(`/leases/${leaseId}/documents`, payload);
      toast.success("Documents added to the lease");
      loadAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add the documents");
      return false;
    }
  };

  const deleteLeaseInfoDoc = async (leaseId, docId) => {
    if (!window.confirm("Remove this supporting document? Make sure you've downloaded it first — this cannot be undone.")) return;
    try {
      await api.delete(`/leases/${leaseId}/info-docs/${docId}`);
      toast.success("Supporting document removed");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove the document");
    }
  };

  const deleteLease = async (leaseId, payload) => {
    try {
      await api.delete(`/leases/${leaseId}`, { data: payload });
      toast.success("Lease permanently deleted");
      loadAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete the lease");
      return false;
    }
  };

  const createLease = async () => {
    if (!leaseForm.lease_doc?.path) {
      toast.error("Please upload the lease agreement PDF.");
      return;
    }
    const documents = [];
    if (leaseForm.lease_doc?.path) documents.push({ doc_type: "lease", name: leaseForm.lease_doc.name, path: leaseForm.lease_doc.path });
    if (leaseForm.lead_paint?.path) documents.push({ doc_type: "lead_paint", name: leaseForm.lead_paint.name, path: leaseForm.lead_paint.path });
    if (leaseForm.smoke_co?.path) documents.push({ doc_type: "smoke_co", name: leaseForm.smoke_co.name, path: leaseForm.smoke_co.path });
    const info_docs = [leaseForm.info1, leaseForm.info2].filter((d) => d?.path).map((d) => ({ name: d.name, path: d.path }));
    setBusy(true);
    try {
      await api.post("/leases", {
        property_id: leaseForm.property_id,
        renter_id: leaseForm.renter_id,
        start_date: leaseForm.start_date,
        end_date: leaseForm.end_date,
        monthly_rent: parseFloat(leaseForm.monthly_rent),
        security_deposit: parseFloat(leaseForm.security_deposit),
        other_deposits: parseFloat(leaseForm.other_deposits || 0),
        pet_deposit: parseFloat(leaseForm.pet_deposit || 0),
        pet_rent: parseFloat(leaseForm.pet_rent || 0),
        first_month_rent: parseFloat(leaseForm.first_month_rent || leaseForm.monthly_rent || 0),
        lease_term_months: parseInt(leaseForm.lease_term_months || 12, 10),
        terms: leaseForm.terms,
        documents,
        info_docs,
      });
      toast.success("Lease package sent for signature");
      setLeaseForm(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create lease");
    } finally {
      setBusy(false);
    }
  };

  const KPIS = [
    { label: "Active Properties", value: stats.properties ?? 0, icon: Building2, tab: "properties" },
    { label: "Pending Applications", value: stats.pending_applications ?? 0, icon: FileText, tab: "applications" },
    { label: "Open Maintenance", value: stats.open_maintenance ?? 0, icon: Wrench, tab: "maintenance" },
    { label: "Rent Collected", value: money(stats.rent_collected ?? 0), icon: DollarSign, tab: "leases" },
  ];

  const set = (k) => (e) => setPropForm({ ...propForm, [k]: e.target.value });

  const overdueApps = applications.filter((a) => a.signed_application?.path && daysSince(a.created_at) >= PURGE_DAYS);
  const overdueLeaseDocs = leases.flatMap((l) => (l.documents || []).filter((d) => d.signed && !d.purged && d.signed_at && daysSince(d.signed_at) >= PURGE_DAYS));
  const overdueCount = overdueApps.length + overdueLeaseDocs.length;

  return {
    user, properties, applications, showings, leases, maintenance,
    propForm, activeTab, setActiveTab, editId, propOpen, setPropOpen, busy,
    leaseForm, setLeaseForm, payout, paypalEmail, setPaypalEmail, venmoHandle, setVenmoHandle,
    couponCode, setCouponCode, billing, declineApp, setDeclineApp, unread, supportUnread,
    activity, contractors, autoPurgeDays, suggestApp, setSuggestApp,
    loadAll, loadUnread, loadSupportUnread, saveAutoPurge, savePayout, connectStripe,
    upgradePlan, redeemCoupon, cancelPlan, reactivatePlan, fmtDate,
    addContractor, deleteContractor, openCreate, openEdit, onImageFiles, removeImage,
    saveProperty, deleteProperty, toggleVisibility, markFilled, markAvailable, relistUnit,
    deleteApplication, bulkPurgeApplications, deleteSignedApplication, deleteSignedLeaseDoc,
    decide, openDecline, confirmDecline, respondShowing, updateMaint, dispatchMaint,
    resendDispatch, rateContractor,
    openLeaseFor, sendReminder, sendInvoice, markRentPaid, createLease,
    endLease, deleteLease, addLeaseDocuments, deleteLeaseInfoDoc,
    printApplication, KPIS, set, overdueApps, overdueLeaseDocs, overdueCount,
  };
}
